// @flow
import './CollectionsTree.css';

import { EventEmitter } from 'events';
import * as React from 'react';
import { DropTarget } from 'react-dnd';
import Store from '../../store/Store';
import CollectionsTreeView, { CollectionTreeNode } from './CollectionsTreeView';
import { ThemeSetup } from '../../themes/ThemeSetup';
import { ZmCollection } from '../../autogenerated/ZoomBridge';
import { LOADED_TREE_COLLECTIONS, COLLECTION_ID, PWD_COLLECTIONS } from '../../store/Constants';
import { createDndConfig } from '../../dnd/reactDndConfig';
import { Button } from '../../common';
import { Separator } from '../../common/form';
import { ALL, MINE, SUBTAB } from '../../util/Constants';
import { COLLECTIONS, SELECTED_COLLECTION } from '../../store/Constants';
import ViewUtil from '../../ViewUtil';
import CollectionUtil from '../../queries/lightbox/CollectionUtil';
import classNames from 'classnames';
import t from '../../i18n/I18n';
import withUniResizableColumn from '../../hoc/withUniReziableColumn';
import StoreListener from '../../store/StoreListener';
const debug = require('../../debug/debug')('wc:assets:CollectionsTree');

export const treeEmitter = new EventEmitter();

const TREE_COLLECTIONS_NAMESPACE = 'COLLECTIONSTREE.CollectionsTree';

type State = {
  // root: ?TreeRootNode,
  root: CollectionTreeNode
};

//const storeItems = [PWD_COLLECTIONS];
//@StoreListener(storeItems)
export class CollectionsTree extends React.Component<Props, State> {
  _treeView: CollectionsTreeView;
  forceUpdateTimer = undefined;
  collections: Array<ZmCollection>;

  constructor(props: Props) {
    super(props);

    this.state = {
      root: this.getNewRootNode(),
      filter: window.gs.collectionOwnerFilter ? window.gs.collectionOwnerFilter : ALL,
      isExpand: true
    };

    // If we're opening the root asset tab view (i.e. not by clicking the back
    // button which should preserve tree view state) then reset tree view state.
    if (Store.get(TREE_COLLECTIONS_NAMESPACE)) {
      debug('[constructor()] clearing collections tree view');
      Store.put(TREE_COLLECTIONS_NAMESPACE, null);
    }
  }

  componentWillMount() {
    const root = Store.get(LOADED_TREE_COLLECTIONS);
    if (root) {
      this.state.root = root;
      Store.put(LOADED_TREE_COLLECTIONS, null);
    }
    this.buildTree();
  }

  componentDidMount() {
    this._isMounted = true;
    treeEmitter.addListener('refreshNodes', this.refreshNodes);
    treeEmitter.addListener('updateCurrentActiveNode', this.updateCurrentActiveNode);
    setTimeout(() => {
      this.refreshNodes();
    }, 1000);
  }

  componentWillUnmount() {
    const { root } = this.state;
    Store.put(LOADED_TREE_COLLECTIONS, root);
    this._isMounted = false;
    treeEmitter.removeListener('refreshNodes', this.refreshNodes);
    treeEmitter.removeListener('updateCurrentActiveNode', this.updateCurrentActiveNode);
  }

  forceUpdateTimerCall = () => {
    if (this.forceUpdateTimer) {
      clearTimeout(this.forceUpdateTimer);
    }
    this.forceUpdateTimer = setTimeout(() => {
      if (this._isMounted) {
        this.forceUpdate();
      }
    }, 500);
  };

  updateCurrentActiveNode = () => {
    if (this._treeView && this._treeView.state.activeNode) this._treeView.state.activeNode.remoteFetch().then();
  };

  refreshNodes = (collectionIds: number[], toBeExpanded = false) => {
    debug('[refreshNodes()] collectionIds: ', collectionIds);
    const expandedNodeIds = this._treeView ? this._treeView.state.expandedNodes : undefined;

    const visit = async (node: CollectionTreeNode) => {
      if (
        _.includes(collectionIds, node.getCollectionId()) ||
        (node.getCollectionId() > 0 && expandedNodeIds && !!expandedNodeIds[node.getCollectionId()])
      ) {
        debug('[refreshNodes()] calling fetchChildren() on collection id: ', node.getCollectionId());
        await node.remoteFetch().then(() => {
          this.forceUpdateTimerCall();
        });

      }

      if (node.hasChildren()) {
        node.getChildren().forEach(visit);
      }
    };

    visit(this.state.root);
    this.forceUpdateTimerCall();
    if (this._treeView && toBeExpanded && this._treeView.updateActiveNodeAndExpandedNodes)
      this._treeView.updateActiveNodeAndExpandedNodes(this.state.root, collectionIds);
  };

  visitFolder = async (node, collectionIds, expandedNodeIds, manualRefresh) => {
    if (
      _.includes(collectionIds, node.getCollectionId()) ||
      (node.getCollectionId() > 0 && expandedNodeIds && !!expandedNodeIds[node.getCollectionId()])
    ) {
      debug('[refreshNodes()] calling fetchChildren() on collection id: ', node.getCollectionId());
      await node.remoteFetch(manualRefresh);
    }

    if (node.hasChildren()) {
      node.getChildren().forEach((n) => this.visitFolder(n, collectionIds, expandedNodeIds, true));
    }
    return;
  };

  updateActiveNode = async (attempt: number = 1) => {
    if (!this._treeView && attempt <= 5) {
      debug('[updateActiveNode()] treeView not ready, trying again in 1s');
      return setTimeout(() => this.updateActiveNode(attempt + 1), 1000);
    }
    if (this._treeView && this._treeView.updateActiveNodeAndExpandedNodes)
      this._treeView.updateActiveNodeAndExpandedNodes(this.state.root, []);
  };

  getNewRootNode(): CollectionTreeNode {
    return new CollectionTreeNode(new ZmCollection().setCollectionId(CollectionUtil.ROOT_COLLECTION_ID).setName(t('COLLECTIONS')), null);
  }

  buildTree = async () => {
    debug('[buildTree()] begin');
    const { root } = this.state;
    if (root.isChildLoaded()) {
      return;
    }
    try {
      const rootNode: CollectionTreeNode = this.getNewRootNode();
      rootNode.remoteFetch().then(
        () => {
          this.setState({ root: rootNode });
          this.updateActiveNode();
        }
      )
    } catch (e) {
      debug(e);
    }
  };

  calculatePwdCollections = (node: CollectionTreeNode) => {
    let pwd_collections = [];
    if (node) {
      let n = node;
      while (n.getCollectionId() > 0 && n && !n.isHide()) {
        pwd_collections.push(n.getCollectionId());
        n = n.getParent();
      }
      pwd_collections = pwd_collections.reverse();
    }
    return pwd_collections;
  };


  onSelect = (node: CollectionTreeNode) => {
    let pwd_collections = this.calculatePwdCollections(node);
    CollectionUtil.switchToCollection(node.getCollection(), pwd_collections, true);
    this.props.onClearSelectedItems();
  };

  getIcon = (node: CollectionTreeNode, isExpand: boolean = false, isActive: Boolean = false) => {
    if (node.isBinCollection())
      return isActive ? ThemeSetup.getImageURL('refactor/icons_active_bin.svg') : 'images/icons_bin.svg';
    else
      return isActive ? ThemeSetup.getImageURL('refactor/icons_coll.svg') : 'images/icons_coll.svg';
  };

  getLabel = (node: CollectionTreeNode) => {
    return node.getCollection().getName();
  };

  getIsLeaf = (node: CollectionTreeNode) => {
    return !node.isBinCollection();
  };

  // decorate = (Component: any) => {
  //   return DropTarget('ASSET', dnd.target.spec, dnd.target.collect)(Component);
  // };

  showAllFiles = async () => {
    this.removeOtherActiveNode();
    let args = { ...window.ARGS };
    delete args.fuid;
    delete args.pwd;
    delete args.collectionId;
    if (ViewUtil.isBrowseSubTabActive() || args.pwd_collections.length > 0) {
      delete args.pwd_collections;
      Store.put(PWD_COLLECTIONS, []);
    }
    args.activeSubTab = SUBTAB.ALL.KEY;
    ViewUtil.setARGS(args, true);
    Store.put(COLLECTION_ID, CollectionUtil.ALL_COLLECTION_ID);
    await CollectionUtil.loadCollectionsInView(CollectionUtil.ALL_COLLECTION_ID);
    this.props.onClearSelectedItems();
  };

  removeOtherActiveNode = () => {
    //Todo : We can do same thing using ref. try to check which one is better efefctive and less vulnerable.
    const treeElements = document.getElementsByName('CollectionsTreeView');
    if (treeElements && treeElements.length > 0) {
      const activeTreeElements = treeElements[0].getElementsByClassName('active');
      if (activeTreeElements && activeTreeElements.length > 0) {
        const element = activeTreeElements[0];
        if (element) {
          element.classList.remove('active');
        }
      }
    }
  }

  toggleAllMineFilter = () => {
    return this.state.filter === ALL ? this.onFilterChange(MINE) : this.onFilterChange(ALL);
  }

  onFilterChange = async (value) => {
    if (this.state.filter !== value) {
      window.gs.collectionOwnerFilter = value;
      this.setState({ filter: value });

      /*if (value === MINE) {
        let collId = CollectionUtil.getBaseCollectionId();
        Store.put(COLLECTION_ID, null);
        ViewUtil.switchToBinId(collId, ViewUtil.getActiveSubTab());
      }*/

      if (this.props.reloadCollection) {
        this.props.reloadCollection();
      }
    }
  }

  render() {
    const { root } = this.state;
    let isAllFilter = this.state.filter == ALL;
    let isExpand = this.state.isExpand;
    if (!root) {
      return null;
    }
    const treeStyle = {
      paddingTop: 15,
      width: isExpand ? 240 : 60
    }

    return (
      <React.Fragment>
        <div className='flex-row'>
          <div name="CollectionsTree" style={treeStyle}>
            <div style={isExpand ? { height: '30px', fontSize: '12px', paddingLeft: '16px', color: 'var(--tree-node-font-color)' } : { height: '30px', display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
              {isExpand ? <span>{t('COLLECTIONS')}</span> : null}
              <img style={isExpand ? { width: '16px', height: '16px', float: 'right', position: 'relative', right: '10px', transform: 'rotateY(180deg)', cursor: 'pointer' } : { width: '16px', height: '16px', cursor: 'pointer' }}
                src='images/icons_expand_left_menu.svg'
                onClick={() => { this.setState({ isExpand: !isExpand }); this.props.treeExpanded(!isExpand); }} />
            </div>
            {
              !window.gs.isExternalUser ?
                <div>
                  <div name="AllAndMine"
                    style={isExpand ? {
                      marginLeft: '10px',
                      marginRight: '10px',
                      marginBottom: '10px',
                      background: 'var(--primary-header-bg)'
                    } : { display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {
                      !isExpand ?
                        <img style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                          src={isAllFilter ? 'images/icons_all.svg' : 'images/icons_mine.svg'}
                          onClick={() => this.toggleAllMineFilter()} /> :
                        <div>
                          <Button
                            className={isAllFilter ? 'active' : ''}
                            icon={
                              isAllFilter
                                ? ThemeSetup.getImageURL('refactor/icons_all.svg')
                                : 'images/icons_all.svg'
                            }
                            style={isAllFilter ? { borderRadius: '5px 0 0 5px' } : {}}
                            text={ALL}
                            onClick={() => this.onFilterChange(ALL)}
                          />
                          <Button
                            className={!isAllFilter ? 'active' : ''}
                            icon={
                              !isAllFilter
                                ? ThemeSetup.getImageURL('refactor/icons_mine.svg')
                                : 'images/icons_mine.svg'
                            }
                            style={!isAllFilter ? { borderRadius: '0 5px 5px 0 ' } : {}}
                            text={MINE}
                            onClick={() => this.onFilterChange(MINE)}
                          />
                        </div>
                    }
                  </div>
                  <Separator />
                </div> : null
            }
            {
              !isExpand ?
                <div className={ViewUtil.isCollectionAllTabActive() ? 'allActive' : ''}
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '62px', cursor: 'pointer' }}
                  onClick={() => this.showAllFiles()}>
                  <img style={{ width: '20px', height: '20px' }}
                    src={ViewUtil.isCollectionAllTabActive() ? ThemeSetup.getImageURL('refactor/icons_all_coll.svg') : 'images/icons_all_coll.svg'} />
                </div> :
                <div name="AllCollectionsTree" className={ViewUtil.isCollectionAllTabActive() ? 'active' : ''}>
                  <div>
                    <Button
                      className={ViewUtil.isCollectionAllTabActive() ? 'active' : ''}
                      icon={
                        ViewUtil.isCollectionAllTabActive() ?
                          ThemeSetup.getImageURL('refactor/icons_all_coll.svg') : 'images/icons_all_coll.svg'
                      }
                      style={{ paddingLeft: '5px' }}
                      text={t('ALL_COLLECTIONS')}
                      onClick={() => this.showAllFiles()}
                    />
                  </div>
                </div>
            }
            {
              !window.gs.isExternalUser ?
                <div>
                  {
                    isExpand ?
                      <div className={classNames("folder")}>
                        <span><img src={ThemeSetup.getImageURL('refactor/icons_folder.svg')} className='folderIcon' /></span>
                        {t('BROWSE_COLLECTIONS')}
                      </div> :
                      <div style={!ViewUtil.isCollectionAllTabActive() ? { display: 'flex', justifyContent: 'center', background: 'var(--tree-active-node-bg)', padding: '10px', cursor: 'pointer' } : { display: 'flex', justifyContent: 'center', padding: '10px', cursor: 'pointer' }}
                        onClick={() => this.onSelect(root)}>
                        <img style={{ width: '20px', height: '20px' }} src={!ViewUtil.isCollectionAllTabActive() ? ThemeSetup.getImageURL('icons_collection_folder.svg')
                          : 'images/icons_folder.svg'} />
                      </div>
                  }
                  {
                    isExpand ? <CollectionsTreeView
                      // showTreeView={this.props.showTreeView}
                      ref={(ref) => (this._treeView = ref)}
                      namespace="CollectionsTree"
                      root={root}
                      onSelect={this.onSelect}
                      getIcon={this.getIcon}
                      getLabel={this.getLabel}
                      getIsLeaf={this.getIsLeaf}
                    //updateTree={this.updateCollectionTree}
                    /> : null
                  }
                </div> : null
            }
          </div>
        </div>
      </React.Fragment >
    );
  }
}

export default CollectionsTree;